// 處理中遮罩：凍結背景捲動，置中顯示進度條 + 滾動 log
// 完成後顯示本次 token 消耗與真實 TWD 成本（不可虛假計算）

import { useEffect, useMemo, useRef } from "react";
import { cn } from "@/lib/utils";
import { formatTime } from "@/lib/utils";
import {
  formatTwd,
  formatUsd,
  shortModelName,
  summarizeClaudeCost,
  summarizeWhisperCost,
} from "@/lib/cost";
import type { JobResultCost, LogMessage } from "@/types";

export type OverlayStatus = "running" | "completed" | "failed";

export interface ProcessingOverlayProps {
  open: boolean;
  status: OverlayStatus;
  /** 0-100，未知時傳 null 走不定型動畫 */
  progress: number | null;
  stepLabel: string;
  logs: LogMessage[];
  cost?: JobResultCost | null;
  errorMessage?: string | null;
  primaryAction?: { label: string; onClick: () => void };
  secondaryAction?: { label: string; onClick: () => void };
}

export function ProcessingOverlay({
  open,
  status,
  progress,
  stepLabel,
  logs,
  cost,
  errorMessage,
  primaryAction,
  secondaryAction,
}: ProcessingOverlayProps) {
  // 鎖背景捲動
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const logRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [logs.length]);

  const indeterminate = progress === null;
  const pct = Math.max(0, Math.min(100, progress ?? 0));

  const headline = useMemo(() => {
    if (status === "failed") return "處理失敗";
    if (status === "completed") return "完成了";
    return "正在處理中⋯⋯";
  }, [status]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 sm:px-6"
      role="dialog"
      aria-modal="true"
      aria-label={headline}
    >
      {/* 背景遮罩 */}
      <div className="absolute inset-0 bg-brand-dark/55 backdrop-blur-sm" />

      {/* 卡片 */}
      <div className="relative w-full max-w-xl bg-white rounded-2xl shadow-2xl border border-brand-gray/15 overflow-hidden">
        <div className="px-7 sm:px-9 pt-8 pb-6">
          <div className="flex items-center gap-3 mb-2">
            {status === "running" && (
              <div className="w-5 h-5 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
            )}
            {status === "completed" && (
              <svg
                className="w-5 h-5 text-brand-green"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
            )}
            {status === "failed" && (
              <svg
                className="w-5 h-5 text-red-700"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4M12 16h0" />
              </svg>
            )}
            <h2 className="font-serif text-xl font-bold text-brand-dark">
              {headline}
            </h2>
            <span
              className={cn(
                "ml-auto text-2xl font-bold num",
                status === "failed"
                  ? "text-red-700"
                  : status === "completed"
                    ? "text-brand-green"
                    : "text-brand-gold",
              )}
            >
              {indeterminate && status === "running"
                ? ""
                : `${Math.round(pct)}%`}
            </span>
          </div>
          <p className="text-xs text-brand-dark/55 leading-relaxed mb-5">
            {status === "running"
              ? "請別關掉這個視窗，處理通常需要 15–60 秒。"
              : status === "completed"
                ? "已經整理好了，下面是這次處理的成本紀錄。"
                : "下面有錯誤訊息，可以重試或回到上一步。"}
          </p>

          {/* 進度條 */}
          <div className="h-2 bg-brand-gray/15 rounded-full overflow-hidden mb-2 relative">
            {indeterminate && status === "running" ? (
              <div className="absolute inset-0 flex">
                <div className="h-full w-1/3 bg-gradient-to-r from-transparent via-brand-gold to-transparent animate-[overlay-indeterminate_1.6s_linear_infinite]" />
              </div>
            ) : (
              <div
                className={cn(
                  "h-full rounded-full transition-all duration-500",
                  status === "failed"
                    ? "bg-red-500"
                    : "bg-gradient-to-r from-brand-gold to-brand-dark",
                )}
                style={{ width: `${Math.max(2, pct)}%` }}
              />
            )}
          </div>

          <div className="flex items-baseline justify-between gap-3 mb-5">
            <span className="text-sm font-medium text-brand-dark truncate">
              {stepLabel}
            </span>
          </div>

          {/* 滾動 log */}
          <div className="text-[10px] uppercase tracking-[0.2em] text-brand-dark/45 mb-2">
            處理紀錄
          </div>
          <div
            ref={logRef}
            className="bg-brand-dark text-brand-cream/90 rounded-lg p-3 max-h-48 sm:max-h-56 overflow-y-auto font-mono text-xs leading-relaxed scroll-smooth"
          >
            {logs.length === 0 ? (
              <div className="text-brand-cream/40 p-1">等待開始⋯⋯</div>
            ) : (
              logs.map((log, i) => (
                <div
                  key={`${log.timestamp}-${i}`}
                  className={cn(
                    "flex gap-2 py-0.5",
                    log.level === "error"
                      ? "text-red-300"
                      : log.level === "warn"
                        ? "text-yellow-300"
                        : log.level === "success"
                          ? "text-brand-green"
                          : "text-brand-cream/85",
                  )}
                >
                  <span className="text-brand-cream/40 num flex-shrink-0">
                    {formatTime(log.timestamp)}
                  </span>
                  <span className="break-words">{log.message}</span>
                </div>
              ))
            )}
          </div>

          {/* 錯誤訊息 */}
          {status === "failed" && errorMessage && (
            <div className="mt-5 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="text-xs font-medium text-red-800 mb-1 uppercase tracking-wider">
                錯誤訊息
              </div>
              <div className="text-sm text-red-700 break-words">
                {errorMessage}
              </div>
            </div>
          )}

          {/* 成本 */}
          {status === "completed" && cost && <CostPanel cost={cost} />}
        </div>

        {/* 底部 CTA */}
        {(primaryAction || secondaryAction) && (
          <div className="px-7 sm:px-9 py-4 bg-brand-cream/40 border-t border-brand-gray/15 flex items-center justify-end gap-3">
            {secondaryAction && (
              <button
                onClick={secondaryAction.onClick}
                className="text-sm text-brand-dark/60 hover:text-brand-dark px-4 py-2"
              >
                {secondaryAction.label}
              </button>
            )}
            {primaryAction && (
              <button
                onClick={primaryAction.onClick}
                className="text-sm font-medium px-5 py-2.5 rounded-full bg-brand-dark text-brand-cream hover:bg-brand-gold transition-colors"
              >
                {primaryAction.label}
              </button>
            )}
          </div>
        )}
      </div>

      {/* 不定型動畫 keyframes */}
      <style>{`
        @keyframes overlay-indeterminate {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
      `}</style>
    </div>
  );
}

// === 成本面板 ===

function CostPanel({ cost }: { cost: JobResultCost }) {
  const { claude, whisper, total_twd, total_usd } = cost;
  const exchange =
    claude?.twd_per_usd ?? whisper?.twd_per_usd ?? 32;

  return (
    <div className="mt-6 p-4 sm:p-5 bg-brand-cream rounded-lg border border-brand-gold/30">
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <div className="text-[11px] uppercase tracking-[0.2em] text-brand-dark/50">
          本次處理成本
        </div>
        <div className="text-[10px] text-brand-dark/45 num">
          匯率 1 USD ≈ NT${exchange.toFixed(2)}
        </div>
      </div>
      <div className="flex items-baseline gap-2 mb-4">
        <span className="text-3xl font-bold text-brand-dark num">
          {formatTwd(total_twd)}
        </span>
        <span className="text-xs text-brand-dark/50 num">
          ({formatUsd(total_usd)})
        </span>
      </div>
      <div className="space-y-2 text-xs">
        {claude && (
          <div className="flex items-start gap-2">
            <span className="inline-block px-1.5 py-0.5 rounded bg-brand-gold/20 text-brand-dark text-[10px] font-medium flex-shrink-0">
              {shortModelName(claude.model)}
            </span>
            <span className="text-brand-dark/75 break-words">
              {summarizeClaudeCost(claude)}
            </span>
          </div>
        )}
        {whisper && (
          <div className="flex items-start gap-2">
            <span className="inline-block px-1.5 py-0.5 rounded bg-brand-gold/20 text-brand-dark text-[10px] font-medium flex-shrink-0">
              Whisper
            </span>
            <span className="text-brand-dark/75 break-words">
              {summarizeWhisperCost(whisper)}
            </span>
          </div>
        )}
      </div>
      <p className="mt-3 text-[10px] text-brand-dark/45 leading-relaxed">
        以 Anthropic 公開報價（Sonnet 4.6 輸入 $3／輸出 $15 per 1M tokens）與
        Cloudflare Workers AI 費率即時計算，匯率取自伺服器設定的 TWD_PER_USD。
      </p>
    </div>
  );
}
